/* Verbatim port of WS.toggleExpand (service/review/index.html ~4203-4235 —
 * the board row's inline events-timeline expansion, `.wsexp`) and
 * WS.renderDrawerExtras (~4497-4562 — the AM-workspace block appended to
 * the asset detail drawer, `.wsdrawerblock`).
 *
 * ── F3c (wave C) lit-html conversion — per the wiki.ts conventions block ──
 * `esc()` dropped throughout. Every write to `.wsexp`/the `.wsdrawerblock`/
 * `[data-wout]` in this file now goes through render() consistently (never
 * mixed with a plain textContent/innerHTML write to the SAME element —
 * see the F3c report on why mixing the two corrupts a later render()).
 * `renderDrawerExtras`'s detached-host body-building converts to render()
 * into `div` before it's appended to `#drawerBody` — same pattern
 * ui/popover.ts (F3b) already established for a freshly-created,
 * not-yet-attached node. Unlike statusMenu/renderImpCanvas's more
 * conservative carve-out, every button here IS a genuinely self-contained,
 * single-element concern (no cross-row/cross-card wiring), so all of it —
 * including the `[data-wsact]` status buttons and the live/facts recheck
 * buttons — converts to `@click` directly in the template, re-resolving
 * the task by id at click time for the `[data-wsact]` buttons (same
 * discipline as ws/board.ts's `actClick`).
 *
 * WS.ICON is now a real `svg\`\`` TemplateResult dictionary (state/
 * workspace.ts) — `${ws.ICON.recheck}`/`${ws.ICON.plus}` are plain lit
 * child bindings (the transitional `unsafeHTML` wrapping this file
 * shipped with is gone; see the WS.ICON conversion commit).
 */
import { html, nothing, render } from 'lit';
import { join } from 'lit/directives/join.js';
import { labelEditor } from './labels.ts';
import { checklistPanel } from './checklist.ts';
import { mentionBlur, mentionInput, mentionKeydown, renderMentionBody } from './mentions.ts';
import { domainOf, slugify } from '../lib/format.ts';
import { closeDrawer, openDrawer } from '../ui/drawer.ts';
import type { Workspace } from '../state/workspace.ts';
import type { WsTask, WsRecheckResponse, WsTaskEvent, WsNote } from '../types/workspace.ts';
import type { NormalizedAsset } from '../types/contract.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';

export async function toggleExpand(ws: Workspace, row: HTMLElement, t: WsTask): Promise<void> {
  const exp = row.querySelector('[data-exp]') as HTMLElement;
  const btn = row.querySelector('[data-act="expand"]');
  if (!exp.classList.contains('hidden')) {
    exp.classList.add('hidden');
    if (btn) btn.setAttribute('aria-expanded', 'false');
    return;
  }
  exp.classList.remove('hidden');
  if (btn) btn.setAttribute('aria-expanded', 'true');
  render(html`<span style="color:var(--muted)">loading…</span>`, exp);
  let evs: WsTaskEvent[] = [];
  try { evs = ((await ws.api<{ events?: WsTaskEvent[] }>(`/tasks/${t.id}/events`)).events) || []; } catch (e) {}
  // P2/C: fetch the full checklist for the drawer editor; sync the task's
  // {done,total} counter from it so the row/card badge is current the next time
  // the row repaints (the server counter refreshes on the next board fetch).
  const clItems = await ws.fetchChecklist(t);
  t.checklist = { done: clItems.filter((i) => i.done).length, total: clItems.length };
  const hint = !ws.probeFor(t) && ws.METHOD_HINTS[t.category || ''];
  const save = async () => {
    const body = (exp.querySelector('textarea') as HTMLTextAreaElement).value.trim(); if (!body) return;
    // `author` STRIPPED (Phase 0 auth, Task 5) — see state/workspace.ts's
    // migrateOnce() comment.
    try {
      await ws.api(`/clients/${ws.clientSlug}/notes`, { method: 'POST',
        body: JSON.stringify({ body,
          target_type: 'task', target_id: String(t.id) }) });
      ws.toast('Note saved.', 'ok');
      toggleExpand(ws, row, t); toggleExpand(ws, row, t);
    } catch (e) { ws.toast('Note failed: ' + (e as Error).message, 'warn'); }
  };
  // T4 watch toggle: watch/unwatch the task, then re-render the expander
  // (collapse+expand, the same reload the note-save uses) so the eye reflects
  // the new state ws.toggleWatch() mutated onto `t`.
  const watch = async () => {
    await ws.toggleWatch(t);
    toggleExpand(ws, row, t); toggleExpand(ws, row, t);
  };
  // P2/L: after a label attach/detach/create, reload the expander (the same
  // collapse+expand the note-save / watch toggles use) so the chips reflect the
  // new t.labels the server returned.
  const reloadLabels = () => { toggleExpand(ws, row, t); toggleExpand(ws, row, t); };
  render(html`${hint ? html`<div class="ev" style="border-left:2px solid var(--azure);padding-left:8px;margin-bottom:10px"><span class="who">how to work this</span> — ${hint}</div>` : nothing}
    ${labelEditor(ws, t, reloadLabels)}
    ${checklistPanel(ws, t, clItems, reloadLabels)}
    ${evs.slice(-12).reverse().map((e) => html`
    <div class="ev"><span class="who">${e.at.slice(0,16).replace('T',' ')} · ${e.author || ''}</span> —
      ${e.kind === 'status' ? html`<b>${ws.STATUS_LABEL[e.from_status ?? '']||e.from_status||''} → ${ws.STATUS_LABEL[e.to_status ?? '']||e.to_status}</b>` : e.kind}
      ${e.body ? html` · ${renderMentionBody(e.body)}` : ''}</div>`)}
    <textarea rows="2" placeholder="add a note for the team… type @ to mention a teammate" @input=${(ev: Event) => mentionInput(ev, ws)} @keydown=${(ev: KeyboardEvent) => mentionKeydown(ev)} @blur=${() => mentionBlur()}></textarea>
    <div style="margin-top:6px;display:flex;gap:6px;align-items:center">
      <button class="wsmini" data-save @click=${save}>Save note</button>
      <button class="wsmini" data-watch
        title=${t.watching ? 'You’re watching this task — click to stop getting its alerts' : 'Watch this task to get its regression / due / stall alerts'}
        style=${t.watching ? 'color:var(--azure)' : ''}
        aria-pressed=${t.watching ? 'true' : 'false'}
        @click=${watch}>${t.watching ? ws.ICON.eye : ws.ICON.eyeOff}${t.watching ? 'Watching' : 'Watch'}${t.watcherCount ? html` · ${t.watcherCount}` : nothing}</button>
    </div>`, exp);
}

export function renderDrawerExtras(ws: Workspace, state: AppPayloadState, a: NormalizedAsset): void {
  if (ws.available !== true) return;
  const body = document.getElementById('drawerBody')!;
  const dom = a.url ? domainOf(a.url) : null;
  const related = ws.tasks.filter((t) =>
    (dom && t.action_key.includes(dom)) ||
    (a.platform && t.action_key.includes(slugify(a.platform).slice(0,24))) ||
    (t.asset_id && t.asset_id === a.id));
  const notes = ws.notes.filter((n) => n.target_type === 'asset' && n.target_id === a.id && !n.resolved_at);
  const div = document.createElement('div');
  div.className = 'wsdrawerblock';
  // assigned once the template below has rendered (data-wout doesn't exist
  // until then); `run`/the buttons that call it are only ever invoked
  // later, on click, by which point this closure variable is populated —
  // same ordering the original used (out computed once, right after
  // div.innerHTML was set, then closed over by run()).
  let out: HTMLElement;
  const run = async (probe: string) => {
    // `author` STRIPPED (Phase 0 auth, Task 5) — see state/workspace.ts's
    // migrateOnce() comment.
    render(html`checking…`, out);
    try {
      const r = await ws.api<WsRecheckResponse>(`/clients/${ws.clientSlug}/recheck`, { method: 'POST',
        body: JSON.stringify({ probe, target: a.url }) });
      const rEv: NonNullable<WsRecheckResponse['evidence']> = r.evidence || {};
      if (r.verdict === 'unavailable') {
        render(html`? unavailable — ${(r.evidence && (rEv.reason || 'fetch failed'))}`, out);
        ws.toast('? Could not check — ' + (r.evidence ? (rEv.reason || 'fetch failed') : '').slice(0,120), '');
      } else if (probe === 'page-live') {
        render(html`${r.verdict === 'observed' ? '✓ live' : '✗ not healthy'} — HTTP ${rEv.status}${rEv.title ? ` · "${String(rEv.title).slice(0,60)}"` : ''}`, out);
        ws.toast((r.verdict === 'observed' ? '✓ Page is live' : '✗ Page not healthy') + ` (HTTP ${rEv.status})`, r.verdict === 'observed' ? 'ok' : 'warn');
      } else {
        const sa = (rEv.sameAs || []);
        render(html`✓ fetched (HTTP ${rEv.status}) — ${sa.length} sameAs link${sa.length===1?'':'s'}${sa.length ? html`:<br>${join(sa.slice(0,8).map((u) => html`${u}`), html`<br>`)}` : ''} · ${rEv.outboundCount || 0} outbound`, out);
      }
    } catch (e) { render(html`recheck failed: ${(e as Error).message}`, out); }
  };
  const noteFlow = async () => {
    const bodyTxt = await ws.ask({ title: 'Note on ' + (a.title || a.id), multiline: true });
    if (!bodyTxt) return;
    // `author` STRIPPED (Phase 0 auth, Task 5) — see state/workspace.ts's
    // migrateOnce() comment.
    try {
      await ws.api(`/clients/${ws.clientSlug}/notes`, { method: 'POST',
        body: JSON.stringify({ body: bodyTxt,
          target_type: 'asset', target_id: a.id }) });
      ws.toast('Note saved.', 'ok');
      ws.notes = ((await ws.api<{ notes?: WsNote[] }>(`/clients/${ws.clientSlug}/notes`)).notes) || [];
      // original: `closeDrawer(); openDrawer(a.id);` (~4559) — both were the
      // globally-wrapped versions (openDrawer's wrap also re-runs
      // WS.renderDrawerExtras, refreshing this very block with the new
      // note). ui/drawer.ts's openDrawer() carries that same call at the
      // bottom of its own body now (state/workspace.ts wiring), so a plain
      // close+reopen here reproduces it exactly.
      closeDrawer(state);
      openDrawer(state, a.id);
    } catch (e) { ws.toast('Note failed: ' + (e as Error).message, 'warn'); }
  };
  render(html`
    <h4>WORKSPACE</h4>
    ${related.map((t) => html`<div style="font-size:12px;margin-bottom:5px;display:flex;gap:8px;align-items:center"><a href="#s7" style="color:var(--azure);flex:1;min-width:0">${t.title}</a> <button class="wsst s-${t.status} pill-sm" data-wsact="${t.id}" title="${ws.STATUS_FULL[t.status]||t.status} — click to change" @click=${(ev: MouseEvent) => {
      ev.stopPropagation();
      const fresh = ws.tasks.find((x) => x.id === t.id);
      if (fresh) ws.statusMenu(ev.currentTarget as HTMLElement, fresh);
    }}>${ws.STATUS_LABEL[t.status]||t.status}</button></div>`)}
    ${notes.map((n) => html`<div style="font-size:12px;color:var(--ink-2);margin-bottom:4px">📝 ${renderMentionBody(n.body)} <span style="color:var(--muted);font-size:10.5px">${n.author||''}</span></div>`)}
    <div style="display:flex;gap:6px;margin-top:8px;flex-wrap:wrap">
      ${a.url ? html`<button class="wsmini" data-w="live" @click=${() => run('page-live')}>${ws.ICON.recheck}Recheck page live</button>
      <button class="wsmini" data-w="facts" title="fetch the page and list its sameAs/outbound links" @click=${() => run('page-facts')}>${ws.ICON.recheck}Recheck links</button>` : nothing}
      <button class="wsmini" data-w="note" @click=${noteFlow}>${ws.ICON.plus}note on this asset</button>
    </div>
    <div data-wout style="font-size:11.5px;color:var(--ink-2);margin-top:8px"></div>`, div);
  out = div.querySelector('[data-wout]') as HTMLElement;
  body.appendChild(div);
}
